import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Card,
  Grid,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { ReactComponent as MenuDots } from "../../../assets/Images/MenuDots.svg";
import { ReactComponent as SuccessIcon } from "../../../assets/Images/SuccessProjectIcon.svg";
import { ReactComponent as ConfirmationIcon } from "../../../assets/icons/Confirmation.svg";
import { ReactComponent as BackIcon } from "../../../assets/Images/WhiteBackIcon.svg";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import { shortenString } from "../../../Constant/helper";
import { swalWarningMessage } from "../../../Constant/swalMessage";
import BaseButton from "../Common/Buttons/BaseButton";
import EmptyState from "../Common/EmptyState";
import CustomScrollbars from "../CommonComponents/CustomScrollbars";
import CommonModal from "../CommonComponents/Modal/CommonModal";
import CommonPopup from "../CommonComponents/PopUpCards/CommonPopup";
import DeletePopup from "../CommonComponents/PopUpCards/DeletePopUp";
import {
  editProjectsAction,
  functionsExecuteAction,
} from "../Projects/Store/projects.action";
import { projectsType } from "../Projects/Store/projects.type";
import ConfirmationPopup from "../CommonComponents/PopUpCards/ConfirmationPopup";

const Functions = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { projectId } = useParams<{ projectId: string }>();
  const reducer: any = useSelector((state) => state);
  const {
    ProjectReducer: { selectedProject },
  } = reducer;

  const [successState, setSuccessState] = useState({
    successPopup: false,
    successPopupHeading: "",
    successPopupMessage: "",
  });
  const { successPopup, successPopupHeading, successPopupMessage } =
    successState;

  const [functionState, setFunctionState] = useState({
    functions: selectedProject?.functions || [],
    functionButtonLoading: false,
    showAddFunctionModal: false,
    showEditFunctionModal: false,
    functionName: "",
    functionCode: "",
    functionPrompt: "",
    deleteModal: false,
    functionIndex: null,
    currentEditIndex: -1,
    showTestConfirm: false,
    currentTestIndex: -1,
  });

  const {
    functions,
    functionButtonLoading,
    showAddFunctionModal,
    showEditFunctionModal,
    functionName,
    functionCode,
    functionPrompt,
    deleteModal,
    functionIndex,
    currentEditIndex,
    showTestConfirm,
    currentTestIndex,
  } = functionState;
  const [menuAnchor, setMenuAnchor] = useState<{
    anchor: HTMLElement | null;
    index: number | null;
  }>({
    anchor: null,
    index: null,
  });
  const [loading, setLoading] = useState(false);

  const onChangehandler = (e) => {
    setFunctionState((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const togglePopup = (heading = "", message = "") => {
    setSuccessState((prev) => ({
      ...prev,
      successPopup: true,
      successPopupHeading: heading,
      successPopupMessage: message,
    }));
  };

  const addFunction = async () => {
    if (
      !functionName?.trim() ||
      !functionCode?.trim() ||
      !functionPrompt?.trim()
    ) {
      swalWarningMessage("Please enter all the fields");
      return;
    }

    setFunctionState((prev) => ({
      ...prev,
      functionButtonLoading: true,
    }));

    const updatedFunctions = [
      ...functions,
      {
        function_name: functionName,
        function_code: functionCode,
        function_prompt: functionPrompt,
      },
    ];

    const res: any = await dispatch(
      editProjectsAction({ functions: updatedFunctions }, projectId)
    );
    if (res) {
      await dispatch({
        type: projectsType.GET_SELECTED_PROJECT,
        payload: [{ ...selectedProject, functions: updatedFunctions }],
      });
      setFunctionState((prev) => ({
        ...prev,
        functions: updatedFunctions,
        showAddFunctionModal: false,
      }));
      togglePopup("Function Added", "Function Added Successfully");
    }
    setFunctionState((prev) => ({
      ...prev,
      functionButtonLoading: false,
      functionName: "",
      functionCode: "",
      functionPrompt: "",
    }));
  };

  const handleClick = (event, index) => {
    setMenuAnchor({ anchor: event.currentTarget, index });
  };

  const handleClose = () => {
    setMenuAnchor({ anchor: null, index: null });
  };

  const deleteConfirmation = (index) => {
    setFunctionState((prev) => ({
      ...prev,
      deleteModal: true,
      functionIndex: index,
    }));
    setMenuAnchor({ anchor: null, index: null });
  };

  const handleDeleteFunction = async () => {
    setLoading(true);

    if (functionIndex === null) return;
    const updatedFunctions = [...functions];
    updatedFunctions.splice(functionIndex, 1);

    const res: any = await dispatch(
      editProjectsAction({ functions: updatedFunctions }, projectId)
    );

    if (res) {
      await dispatch({
        type: projectsType.GET_SELECTED_PROJECT,
        payload: [{ ...selectedProject, functions: updatedFunctions }],
      });
      setFunctionState((prev) => ({
        ...prev,
        functions: updatedFunctions,
        deleteModal: false,
        functionIndex: null,
      }));
      togglePopup("Function Deleted", "Function Deleted Successfully");
    }
    setLoading(false);
  };

  const editFunction = async () => {
    if (
      !functionName?.trim() ||
      !functionCode?.trim() ||
      !functionPrompt?.trim()
    ) {
      swalWarningMessage("Please enter all the fields");
      return;
    }

    if (currentEditIndex === -1) {
      return;
    }

    setFunctionState((prev) => ({
      ...prev,
      functionButtonLoading: true,
    }));

    const updatedFunctions = [...functions];
    updatedFunctions[currentEditIndex] = {
      function_name: functionName,
      function_code: functionCode,
      function_prompt: functionPrompt,
    };

    const res: any = await dispatch(
      editProjectsAction({ functions: updatedFunctions }, projectId)
    );

    if (res) {
      await dispatch({
        type: projectsType.GET_SELECTED_PROJECT,
        payload: [{ ...selectedProject, functions: updatedFunctions }],
      });
      setFunctionState((prev) => ({
        ...prev,
        functions: updatedFunctions,
        showEditFunctionModal: false,
        currentEditIndex: -1,
      }));
      togglePopup("Function Updated", "Function Updated Successfully");
    }

    setFunctionState((prev) => ({
      ...prev,
      functionButtonLoading: false,
      functionName: "",
      functionCode: "",
      functionPrompt: "",
    }));
  };

  const openEditModal = (index) => {
    const { function_name, function_code, function_prompt } =
      functions?.[index];
    setFunctionState((prev) => ({
      ...prev,
      functionName: function_name,
      functionCode: function_code,
      functionPrompt: function_prompt,
      currentEditIndex: index,
      showEditFunctionModal: true,
    }));
  };

  const handleTestFunction = (index) => {
    setFunctionState((prev) => ({
      ...prev,
      showTestConfirm: true,
      currentTestIndex: index,
    }));
  };

  const handleConfirmTest = async () => {
    const { function_name } = functions?.[currentTestIndex];
    const functionPayload = {
      function_name,
      function_kwargs: {},
      call_params: {},
      call_uuid: "",
      project_id: projectId,
    };
    const res: any = await dispatch(functionsExecuteAction(functionPayload));
    if (res) {
      setFunctionState((prev) => ({
        ...prev,
        showTestConfirm: false,
        currentTestIndex: -1,
      }));
      togglePopup("Test Started", "Test Started Successfully");
    }
  };

  return (
    <>
      <Box>
        <Box className="flex-between">
          <Typography className="mb-2 font-20-grey">Functions</Typography>

          <Box className="flex-align-center" gap={1}>
            <BaseButton
              title="Add Function"
              variant="outlined"
              handleClick={() =>
                setFunctionState((prev) => ({
                  ...prev,
                  showAddFunctionModal: true,
                }))
              }
              styles={{ px: 2, fontSize: 12 }}
            />
            <BaseButton
              title="Back"
              variant="contained"
              handleClick={() =>
                history.push(`/account/project/${projectId}/call-settings`)
              }
              startIcon={<BackIcon className="back-icon" />}
              styles={{ px: 2, fontSize: 12 }}
            />
          </Box>
        </Box>

        <Box mt={2}>
          <Grid container spacing={2}>
            {functions?.length > 0 ? (
              functions?.map((func, index) => (
                <Grid key={index} size={{ xs: 12, md: 6, lg: 4 }}>
                  <Card className="px-3 pt-3" sx={commonStyles.card}>
                    <Box className="code-container flex-between">
                      <Typography
                        className="font-16-700-grey"
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {shortenString(func?.function_name, 25)}
                      </Typography>
                      <Box>
                        <BaseButton
                          title={<MenuDots title="More" />}
                          variant="outlined"
                          handleClick={(e) => handleClick(e, index)}
                          styles={commonStyles.iconBtn}
                        />
                        <Menu
                          id={`menu-${index}`}
                          anchorEl={menuAnchor.anchor}
                          open={menuAnchor.index === index}
                          onClose={handleClose}
                          slotProps={{
                            paper: {
                              sx: commonStyles.menu,
                            },
                          }}
                        >
                          <MenuItem
                            sx={commonStyles.menuItem}
                            onClick={() => {
                              deleteConfirmation(index);
                              handleClose();
                            }}
                          >
                            Delete
                          </MenuItem>
                          <MenuItem
                            sx={commonStyles.menuItem}
                            onClick={() => {
                              openEditModal(index);
                              handleClose();
                            }}
                          >
                            Edit
                          </MenuItem>
                          <MenuItem
                            sx={commonStyles.menuItem}
                            onClick={() => {
                              handleTestFunction(index);
                              handleClose();
                            }}
                          >
                            Test
                          </MenuItem>
                        </Menu>
                      </Box>
                    </Box>
                    <pre className="code-block">
                      <CustomScrollbars height="17vh">
                        <code>{func?.function_code}</code>
                      </CustomScrollbars>
                    </pre>
                  </Card>
                </Grid>
              ))
            ) : (
              <EmptyState message="No Functions Found" />
            )}
          </Grid>
        </Box>
      </Box>

      {(showAddFunctionModal || showEditFunctionModal) && (
        <CommonModal
          heading={
            (showAddFunctionModal && "Add Function") ||
            (showEditFunctionModal && "Edit Function")
          }
          show={showAddFunctionModal || showEditFunctionModal}
          size={650}
          spin={functionButtonLoading}
          handleSave={
            (showAddFunctionModal && addFunction) ||
            (showEditFunctionModal && editFunction)
          }
          toggle={() =>
            setFunctionState((prev) => ({
              ...prev,
              showAddFunctionModal: false,
              showEditFunctionModal: false,
              currentEditIndex: -1,
              functionName: "",
              functionCode: "",
              functionPrompt: "",
            }))
          }
          body={
            <FunctionBody
              handleChange={onChangehandler}
              funcState={functionState}
            />
          }
        />
      )}
      {deleteModal && (
        <DeletePopup
          message="Are you sure want to delete this function ?"
          show={deleteModal}
          toggle={() =>
            setFunctionState((prev) => ({ ...prev, deleteModal: false }))
          }
          handleDelete={handleDeleteFunction}
          spin={loading}
          heading="Delete Function"
        />
      )}

      {successPopup && (
        <CommonPopup
          message={successPopupMessage}
          show={successPopup}
          toggle={() =>
            setSuccessState((prev) => ({
              ...prev,
              successPopup: false,
              successPopupHeading: "",
              successPopupMessage: "",
            }))
          }
          Icon={SuccessIcon}
          heading={successPopupHeading}
        />
      )}

      <ConfirmationPopup
        show={showTestConfirm}
        toggle={() =>
          setFunctionState((prev) => ({
            ...prev,
            showTestConfirm: false,
            currentTestIndex: -1,
          }))
        }
        handleSave={handleConfirmTest}
        heading="Run Test"
        message="Are you sure you want to test this function?"
        confirmText="Test"
        cancelText="Cancel"
        Icon={ConfirmationIcon}
      />
    </>
  );
};

export default Functions;

const FunctionBody = ({ funcState, handleChange }) => {
  const { functionName, functionCode, functionPrompt } = funcState;
  return (
    <>
      <Box className="mt-2">
        <Box className="mb-2">
          <Typography className="font-14-grey mb-1">Function Name</Typography>
          <TextField
            name="functionName"
            value={functionName}
            type="text"
            onChange={handleChange}
            placeholder="Enter Function Name"
            variant="outlined"
            fullWidth
            sx={commonStyles.inputField}
          />
        </Box>
        <Box className="mb-2">
          <Typography className="font-14-grey mb-1">Function Code</Typography>
          <TextField
            id="chat-focus"
            name="functionCode"
            value={functionCode}
            fullWidth
            multiline
            minRows={4}
            maxRows={4}
            onChange={handleChange}
            slotProps={{
              htmlInput: {
                style: { fontSize: "13px" },
                placeholder: "Enter Function Code",
              },
            }}
            sx={commonStyles.inputField}
          />
        </Box>
        <Box className="mb-2">
          <Typography className="font-14-grey mb-1">Function Prompt</Typography>
          <TextField
            id="chat-focus"
            name="functionPrompt"
            value={functionPrompt}
            fullWidth
            multiline
            minRows={4}
            maxRows={4}
            onChange={handleChange}
            slotProps={{
              htmlInput: {
                style: { fontSize: "13px" },
                placeholder: "Enter Function Prompt",
              },
            }}
            sx={commonStyles.inputField}
          />
        </Box>
      </Box>
    </>
  );
};
